import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { Language } from 'src/app/core/data-models/identity.data.models';
import * as hijriMoment from 'moment-hijri';

export interface CashStatementDialogData {
  investorId: string;
  type: 'excel' | 'pdf';
}

@Component({
  selector: 'app-cash-statement-dialog',
  templateUrl: './cash-statement-dialog.component.html',
  styleUrls: ['./cash-statement-dialog.component.css']
})
export class CashStatementDialogComponent implements OnInit {
  fromDate: Date | null = null;
  toDate: Date | null = null;
  maxDate: Date = new Date();
  validationError: string = '';

  dateType: 'hijri' | 'gregorian' = 'gregorian';
  hijriDays: number[] = Array.from({ length: 30 }, (_, i) => i + 1);
  hijriMonths = [
    { value: 1, label: 'Muharram' },
    { value: 2, label: 'Safar' },
    { value: 3, label: "Rabi' al-Awwal" },
    { value: 4, label: "Rabi' al-Thani" },
    { value: 5, label: 'Jumada al-Ula' },
    { value: 6, label: 'Jumada al-Thaniyah' },
    { value: 7, label: 'Rajab' },
    { value: 8, label: "Sha'ban" },
    { value: 9, label: 'Ramadan' },
    { value: 10, label: 'Shawwal' },
    { value: 11, label: "Dhu al-Qa'dah" },
    { value: 12, label: 'Dhu al-Hijjah' },
  ];
  hijriYears: number[] = [];

  fromHijriDay: number | null = null;
  fromHijriMonth: number | null = null;
  fromHijriYear: number | null = null;
  toHijriDay: number | null = null;
  toHijriMonth: number | null = null;
  toHijriYear: number | null = null;
  fromHijriDate: string | null = null;
  toHijriDate: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<CashStatementDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CashStatementDialogData,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    hijriMoment.locale('en');
    this.hijriYears = this.getHijriYears();
  }

  getHijriYears(): number[] {
    const currentHijriYear = parseInt(hijriMoment().format('iYYYY'));
    const startYear = currentHijriYear - 100;
    const endYear = currentHijriYear;
    const length = endYear - startYear + 1;
    return Array.from({ length }, (_, i) => startYear + i).reverse();
  }

  onDateTypeChange(): void {
    this.fromDate = null;
    this.toDate = null;
    this.fromHijriDay = null;
    this.fromHijriMonth = null;
    this.fromHijriYear = null;
    this.toHijriDay = null;
    this.toHijriMonth = null;
    this.toHijriYear = null;
    this.fromHijriDate = null;
    this.toHijriDate = null;
    this.validationError = '';
  }

  updateFromHijriDate(): void {
    this.fromHijriDate = this.buildHijriDateString(this.fromHijriDay, this.fromHijriMonth, this.fromHijriYear);
    this.onDateChange();
  }

  updateToHijriDate(): void {
    this.toHijriDate = this.buildHijriDateString(this.toHijriDay, this.toHijriMonth, this.toHijriYear);
    this.onDateChange();
  }

  private buildHijriDateString(day: number | null, month: number | null, year: number | null): string | null {
    if (!day || !month || !year) {
      return null;
    }
    return `${year}/${month}/${day}`;
  }

  onDateChange(): void {
    this.validateDates();
  }

  validateDates(): boolean {
    this.validationError = '';

    if (this.dateType === 'gregorian') {
      if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
        this.validationError = 'To Date cannot be earlier than From Date';
        return false;
      }
      return true;
    }

    if (this.fromHijriDate && this.toHijriDate) {
      const from = hijriMoment(this.fromHijriDate, 'iYYYY/iM/iD');
      const to = hijriMoment(this.toHijriDate, 'iYYYY/iM/iD');
      if (from.isValid() && to.isValid() && to.toDate() < from.toDate()) {
        this.validationError = 'To Date cannot be earlier than From Date';
        return false;
      }
    }
    return true;
  }

  isFormValid(): boolean {
    return this.validationError === '';
  }

  download(language: 'ar' | 'en') {
    if (!this.isFormValid()) {
      return;
    }

    const languageEnum: Language =
      language === 'ar' ? Language.Ar_SA : Language.En_US;

    this.dialogRef.close({
      fromDate: this.dateType === 'hijri' ? this.fromHijriDate : this.fromDate,
      toDate: this.dateType === 'hijri' ? this.toHijriDate : this.toDate,
      language: languageEnum,
      calendarType: this.dateType === 'hijri' ? 'Hijri' : 'Gregorian'
    });
  }

  cancel() {
    this.dialogRef.close();
  }
}
